import React, { useEffect, useRef, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { createPortal } from "react-dom";
import { Transition } from "react-transition-group";
import { useSelector } from "react-redux";
import { useTranslation } from "@helpers/translate";
import {
  addSlashToSymbol,
  formatNormalizedPrice,
  formatOrderDateTime,
} from "@helpers/formatOrders";
import ModalBackground from "@assets/icons/ModalBackground";
import CopyIconSecondary from "@assets/icons/CopyIconSecondary";
import DownloadIcon from "@assets/icons/DownloadIcon";
import MailIcon from "@assets/icons/MailIcon";
import XIcon from "@assets/icons/XIcon";
import TelegramIcon from "@assets/icons/TelegramIcon";
import FacebookIcon from "@assets/icons/FacebookIcon";
import LinkedInIcon from "@assets/icons/LinkedInIcon";
import MechIcon from "@assets/icons/MechIcon";
import MoreIcon from "@assets/icons/MoreIcon";
import CrossIcon from "@assets/icons/CrossIcon";
import {
  executeShareAction,
  MOBILE_SHARE_ACTIONS,
  SHARE_ACTIONS,
} from "./shareActions";

import { LogoIcon } from "@components/library";

import { TRADE_MODAL_MOBILE_QUERY } from "../TradeModals/constants";
import "./Share.sass";

export const Share = ({ active, setActive, order, pnl, markPrice, placedBy }) => {
  const popupRef = useRef(null);
  const shareCardRef = useRef(null);
  const [activeActions, setActiveActions] = useState([]);
  const { __, __raw } = useTranslation();
  const rawDesc = __raw("futures.share.description");
  const desc = Array.isArray(rawDesc) ? rawDesc : [];
  const normalizedSide = String(order?.side ?? "").toLowerCase();
  const isBuySide = normalizedSide === "long" || normalizedSide === "buy";
  const createdAtText = formatOrderDateTime(order?.created_at);
  const isMobile = useMediaQuery(TRADE_MODAL_MOBILE_QUERY);
  const { full_name = "", email = "" } = useSelector((state) => state.user.user || {});
  const username = placedBy || full_name || email || "mech";

  const shareOptionsDesctop = [
    {
      icon: CopyIconSecondary,
      label: __("futures.copy"),
      action: SHARE_ACTIONS.COPY,
    },
    {
      icon: DownloadIcon,
      label: __("futures.save"),
      action: SHARE_ACTIONS.DOWNLOAD,
    },
    {
      icon: MailIcon,
      label: __("futures.email"),
      action: SHARE_ACTIONS.EMAIL,
    },
    {
      icon: XIcon,
      label: "X",
      action: SHARE_ACTIONS.SHARE_X,
    },
    {
      icon: TelegramIcon,
      label: __("futures.telegram"),
      action: SHARE_ACTIONS.SHARE_TELEGRAM,
    },
    {
      icon: FacebookIcon,
      label: "Facebook",
      action: SHARE_ACTIONS.SHARE_FACEBOOK,
    },
    {
      icon: LinkedInIcon,
      label: "LinkedIn",
      action: SHARE_ACTIONS.SHARE_LINKEDIN,
    },
    {
      icon: MoreIcon,
      label: __("futures.more"),
      action: SHARE_ACTIONS.MORE,
    },
  ];

  const shareOptionsMobile = shareOptionsDesctop.filter(({ action }) =>
    MOBILE_SHARE_ACTIONS.includes(action),
  );

  const shareOptions = isMobile ? shareOptionsMobile : shareOptionsDesctop;

  const closeModal = () => {
    setActiveActions([]);
    setActive(false);
  };
  const onShareAction = async (action) => {
    await executeShareAction({
      action,
      cardElement: shareCardRef.current,
      order,
      pnl,
    });

    setActiveActions((prev) =>
      prev.includes(action) ? prev : [...prev, action],
    );
  };

  useEffect(() => {
    if (!active) return undefined;
    document.body.classList.add("share-modal-open");
    return () => document.body.classList.remove("share-modal-open");
  }, [active]);

  const modalContent = (
    <Transition
      nodeRef={popupRef}
      in={active}
      timeout={300}
      mountOnEnter
      unmountOnExit
    >
      <div
        ref={popupRef}
        className={`popup popup--share ${active ? "active" : ""}`}
        onClick={closeModal}
      >
        <div className="popup__content" onClick={(e) => e.stopPropagation()}>
          <div className="sharePopup">
            <div className="sharePopup__content">
              <div className="sharePopup__head">
                <h3 className="sharePopup__title">{__("futures.share")}</h3>
                <button
                  type="button"
                  className="sharePopup__close"
                  onClick={closeModal}
                  aria-label={__("futures.trade_modals.close")}
                >
                  <CrossIcon size={20} />
                </button>
              </div>

              <div className="sharePopup__body">
                <div className="sharePopup__left">
                  <div className="sharePopup__left-wrapper" ref={shareCardRef}>
                    <ModalBackground />

                    <div className="sharePopup__left-content">
                      <div className="sharePopup__left-header">
                        <div className="sharePopup__left-user">
                          <MechIcon className="sharePopup__left-user-icon" />
                          <div className="sharePopup__left-user-info">
                            <span>{username}</span>
                            <span>{createdAtText}</span>
                          </div>
                        </div>
                        <div className="sharePopup__left-contract">
                          <span className="sharePopup__symbol">
                            {addSlashToSymbol(order?.symbol)} {__("futures.perpetual")}
                          </span>

                          <span
                            className={`sharePopup__side ${
                              isBuySide
                                ? "sharePopup__side--buy"
                                : "sharePopup__side--sell"
                            }`}
                          >
                            {isBuySide
                              ? __("futures.modal.buy")
                              : __("futures.modal.sell")}

                            <span className="sharePopup__leverage">
                             {' '} | {order.leverage}x
                            </span>
                          </span>
                        </div>

                        <div
                          className={`sharePopup__left-price ${
                            Number(pnl) >= 0 ? "is-profit" : "is-loss"
                          }`}
                        >
                          {pnl} <span>USDT</span>
                        </div>

                        <div className="sharePopup__left-prices">
                          <div>
                            <span className="sharePopup__left-label">
                              {__("futures.entry_price")}
                            </span>
                            <span className="sharePopup__left-value">
                              {formatNormalizedPrice(order.entry_price)}
                            </span>
                          </div>
                          <div>
                            <span className="sharePopup__left-label">
                              {order.status === "Closed"
                                ? __("futures.exit_price")
                                : __("futures.mark_price")}
                            </span>

                            <span className="sharePopup__left-value">
                              {order.status === "Closed"
                                ? formatNormalizedPrice(order.closed_price)
                                : formatNormalizedPrice(markPrice)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="sharePopup__left-footer">
                        <div>
                          <div className="sharePopup__left-logo">
                            <LogoIcon forceFullLogo />
                          </div>
                          <span className="sharePopup__left-futures">
                            FUTURES
                          </span>
                          <span className="sharePopup__left-description">
                            <span className="sharePopup__text sharePopup__text--default">
                              {desc[0]} {" "}
                            </span>
                            <span className="sharePopup__text sharePopup__text--accent">
                              {desc[1]} {" "}
                            </span>
                            <span className="sharePopup__text sharePopup__text--default">
                              {desc[2]}
                            </span>
                          </span>
                        </div>
                        <div className="sharePopup__left-qr-code">
                          <img src="/qr.svg" alt="QR code" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sharePopup__right">
                  {shareOptions.map(({ icon: Icon, label, action }) => (
                    <div key={action} className="sharePopup__right-item">
                      <button
                        type="button"
                        className={`sharePopup__right-icon ${
                          activeActions.includes(action) ? "is-active" : ""
                        }`}
                        onClick={() => onShareAction(action)}
                        aria-label={label}
                        // Share actions are disabled for now
                        disabled
                      >
                        <Icon className="sharePopup__right-svg" />
                      </button>
                      <span className="sharePopup__right-label">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
};
